// Column-spec builders for each report table/panel. Each takes optimumRanges
// (from the ingest payload) and returns the column array consumed by FlagTable.vue.
// Kept as functions (not static arrays) because column `range` values come from
// the per-report optimum_ranges, not fixed data.

export function macroNutrientColumns(optimumRanges) {
	return [
		{ label: 'Phosphorus', unit: 'ppm', get: s => s.phosphorus && s.phosphorus.value_ppm, flag: s => s.flags && s.flags.phosphorus, range: optimumRanges.phosphorus_ppm },
		{ label: 'Potassium', unit: 'ppm', get: s => s.potassium_ppm, flag: s => s.flags && s.flags.potassium, range: optimumRanges.potassium_ppm },
		{ label: 'Calcium', unit: 'ppm', get: s => s.calcium_ppm, flag: s => s.flags && s.flags.calcium, range: optimumRanges.calcium_ppm },
		{ label: 'Magnesium', unit: 'ppm', get: s => s.magnesium_ppm, flag: s => s.flags && s.flags.magnesium, range: optimumRanges.magnesium_ppm },
		{ label: 'Zinc', unit: 'ppm', get: s => s.zinc_ppm && s.zinc_ppm.value, flag: s => s.flags && s.flags.zinc, range: optimumRanges.zinc_ppm },
		{ label: 'Manganese', unit: 'ppm', get: s => s.manganese_ppm && s.manganese_ppm.value, flag: s => s.flags && s.flags.manganese, range: optimumRanges.manganese_ppm },
		{ label: 'Copper', unit: 'ppm', get: s => s.copper_ppm && s.copper_ppm.value, flag: s => s.flags && s.flags.copper, range: optimumRanges.copper_ppm },
		{ label: 'Iron', unit: 'ppm', get: s => s.iron_ppm && s.iron_ppm.value, flag: s => s.flags && s.flags.iron, range: optimumRanges.iron_ppm }
	];
}

export function soilCharacteristicsColumns(optimumRanges) {
	return [
		{ label: 'Soil pH', unit: '', get: s => s.ph, flag: s => s.flags && s.flags.ph, range: optimumRanges.ph },
		{ label: 'Sodium', unit: 'ppm', get: s => s.sodium_ppm },
		{ label: 'Soluble Salts', unit: 'mmhos/cm', get: s => s.soluble_salts_mmhos, flagFromComputed: 'soluble_salts_mmhos', range: optimumRanges.soluble_salts_mmhos },
		{ label: 'Organic Matter', unit: '%', get: s => s.organic_matter_pct, flag: s => s.flags && s.flags.organic_matter, range: optimumRanges.organic_matter_pct },
		{ label: 'CEC', unit: 'meq/100g', get: s => s.cec_meq },
		{ label: '% Hydrogen', unit: '%', get: s => s.base_saturation && s.base_saturation.hydrogen_pct },
		{ label: '% Sodium', unit: '%', get: s => s.base_saturation && s.base_saturation.sodium_pct },
		{ label: '% Potassium', unit: '%', get: s => s.base_saturation && s.base_saturation.potassium_pct },
		{ label: '% Calcium', unit: '%', get: s => s.base_saturation && s.base_saturation.calcium_pct },
		{ label: '% Magnesium', unit: '%', get: s => s.base_saturation && s.base_saturation.magnesium_pct }
	];
}

export function boronSulfurColumns(optimumRanges) {
	return [
		{ label: 'Boron', unit: 'ppm', get: s => s.boron_ppm && s.boron_ppm.value, flag: s => s.flags && s.flags.boron, range: optimumRanges.boron_ppm },
		{ label: 'Sulfur', unit: 'ppm', get: s => s.sulfur_ppm && s.sulfur_ppm.value, flag: s => s.flags && s.flags.sulfur, range: optimumRanges.sulfur_ppm }
	];
}

/* ---- Turf Elite Plus add-on panels --------------------------------
   None of these have Andersons optimum ranges to flag against (same
   situation as Sodium/CEC/% base saturation above), so they render as
   plain value tables. Each one only appears if the batch actually has
   data for it — see groupHasData() in dataAvailability.js. ------------ */

export function soilPhysicalHealthColumns() {
	return [
		{ label: 'Sand', unit: '%', get: s => s.soil_physical && s.soil_physical.sand_pct },
		{ label: 'Silt', unit: '%', get: s => s.soil_physical && s.soil_physical.silt_pct },
		{ label: 'Clay', unit: '%', get: s => s.soil_physical && s.soil_physical.clay_pct },
		{ label: 'Texture', unit: '', get: s => s.soil_physical && s.soil_physical.texture },
		{ label: 'Bulk Density', unit: 'g/cm³', get: s => s.soil_health && s.soil_health.bulk_density_g_cm3 },
		{ label: 'Soil Health Score', unit: '', get: s => s.soil_health && s.soil_health.score },
		{ label: 'Solvita CO2 Respiration', unit: 'ppm', get: s => s.soil_health && s.soil_health.solvita_co2_respiration_ppm },
		{ label: 'C:N Ratio', unit: '', get: s => s.soil_health && s.soil_health.cn_ratio }
	];
}

export function saturatedExtractColumns() {
	return [
		{ label: 'EC', unit: 'mmhos/cm', get: s => s.saturated_paste_extract && s.saturated_paste_extract.ec_mmhos_cm },
		{ label: 'Saturation', unit: '%', get: s => s.saturated_paste_extract && s.saturated_paste_extract.saturation_pct },
		{ label: 'Na', unit: 'meq/L', get: s => s.saturated_paste_extract && s.saturated_paste_extract.na_meq_l },
		{ label: 'Ca', unit: 'meq/L', get: s => s.saturated_paste_extract && s.saturated_paste_extract.ca_meq_l },
		{ label: 'Mg', unit: 'meq/L', get: s => s.saturated_paste_extract && s.saturated_paste_extract.mg_meq_l },
		{ label: 'K', unit: 'meq/L', get: s => s.saturated_paste_extract && s.saturated_paste_extract.k_meq_l },
		{ label: 'NH4', unit: 'ppm', get: s => s.saturated_paste_extract && s.saturated_paste_extract.nh4_ppm },
		{ label: 'NO3', unit: 'ppm', get: s => s.saturated_paste_extract && s.saturated_paste_extract.no3_ppm },
		{ label: 'PO4', unit: 'ppm', get: s => s.saturated_paste_extract && s.saturated_paste_extract.po4_ppm },
		{ label: 'HCO3', unit: 'ppm', get: s => s.saturated_paste_extract && s.saturated_paste_extract.hco3_ppm },
		{ label: 'SO4', unit: 'ppm', get: s => s.saturated_paste_extract && s.saturated_paste_extract.so4_ppm },
		{ label: 'Cl', unit: 'ppm', get: s => s.saturated_paste_extract && s.saturated_paste_extract.cl_ppm },
		{ label: 'BO3', unit: 'ppm', get: s => s.saturated_paste_extract && s.saturated_paste_extract.bo3_ppm },
		{ label: 'SAR', unit: '', get: s => s.saturated_paste_extract && s.saturated_paste_extract.sar }
	];
}

export function waterSolubleColumns() {
	return [
		{ label: 'Carbon', unit: 'ppm', get: s => s.water_soluble && s.water_soluble.carbon_ppm },
		{ label: 'Total N', unit: 'ppm', get: s => s.water_soluble && s.water_soluble.total_n_ppm },
		{ label: 'Nitrate-N', unit: 'ppm', get: s => s.water_soluble && s.water_soluble.nitrate_n_ppm },
		{ label: 'Ammoniacal-N', unit: 'ppm', get: s => s.water_soluble && s.water_soluble.ammoniacal_n_ppm },
		{ label: 'Orthophosphate-P', unit: 'ppm', get: s => s.water_soluble && s.water_soluble.orthophosphate_p_ppm },
		{ label: 'Saturation', unit: '%', get: s => s.water_soluble && s.water_soluble.saturation_pct },
		{ label: 'Mineralizable N', unit: 'lbs/ac', get: s => s.water_soluble && s.water_soluble.mineralizable_n_lbs_ac },
		{ label: 'Mineralizable P', unit: 'lbs/ac', get: s => s.water_soluble && s.water_soluble.mineralizable_p_lbs_ac }
	];
}

export function h3aExtractableColumns() {
	return [
		{ label: 'Orthophosphate-P', unit: 'ppm', get: s => s.h3a_extractable && s.h3a_extractable.orthophosphate_p_ppm },
		{ label: 'Phosphorus', unit: 'ppm', get: s => s.h3a_extractable && s.h3a_extractable.phosphorus_ppm },
		{ label: 'Potassium', unit: 'ppm', get: s => s.h3a_extractable && s.h3a_extractable.potassium_ppm },
		{ label: 'Calcium', unit: 'ppm', get: s => s.h3a_extractable && s.h3a_extractable.calcium_ppm },
		{ label: 'Iron', unit: 'ppm', get: s => s.h3a_extractable && s.h3a_extractable.iron_ppm },
		{ label: 'Aluminum', unit: 'ppm', get: s => s.h3a_extractable && s.h3a_extractable.aluminum_ppm },
		{ label: 'P:(Al+Fe)', unit: '', get: s => s.h3a_extractable && s.h3a_extractable.p_to_al_fe_ratio },
		{ label: 'P:Ca', unit: '', get: s => s.h3a_extractable && s.h3a_extractable.p_to_ca_ratio },
		{ label: 'Ca:(Al+Fe)', unit: '', get: s => s.h3a_extractable && s.h3a_extractable.ca_to_al_fe_ratio }
	];
}

export function totalCombustionColumns() {
	return [
		{ label: 'Carbon', unit: '%', get: s => s.total_combustion && s.total_combustion.carbon_pct },
		{ label: 'Nitrogen', unit: '%', get: s => s.total_combustion && s.total_combustion.nitrogen_pct },
		{ label: 'C:N Ratio', unit: '', get: s => s.total_combustion && s.total_combustion.cn_ratio },
		{ label: 'Organic Carbon', unit: '%', get: s => s.total_combustion && s.total_combustion.organic_carbon_pct },
		{ label: 'Inorganic Carbon', unit: '%', get: s => s.total_combustion && s.total_combustion.inorganic_carbon_pct },
		{ label: 'Bulk Density', unit: 'g/cm³', get: s => s.total_combustion && s.total_combustion.bulk_density_g_cm3 }
	];
}

export function profileNitrogenColumns() {
	return [
		{ label: 'Nitrate D1', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.nitrate_ppm_by_depth && s.profile_nitrogen.nitrate_ppm_by_depth[0] },
		{ label: 'Nitrate D2', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.nitrate_ppm_by_depth && s.profile_nitrogen.nitrate_ppm_by_depth[1] },
		{ label: 'Nitrate D3', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.nitrate_ppm_by_depth && s.profile_nitrogen.nitrate_ppm_by_depth[2] },
		{ label: 'Ammonium D1', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.ammonium_ppm_by_depth && s.profile_nitrogen.ammonium_ppm_by_depth[0] },
		{ label: 'Ammonium D2', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.ammonium_ppm_by_depth && s.profile_nitrogen.ammonium_ppm_by_depth[1] },
		{ label: 'Ammonium D3', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.ammonium_ppm_by_depth && s.profile_nitrogen.ammonium_ppm_by_depth[2] },
		{ label: 'Chloride D1', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.chloride_ppm_by_depth && s.profile_nitrogen.chloride_ppm_by_depth[0] },
		{ label: 'Chloride D2', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.chloride_ppm_by_depth && s.profile_nitrogen.chloride_ppm_by_depth[1] },
		{ label: 'Chloride D3', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.chloride_ppm_by_depth && s.profile_nitrogen.chloride_ppm_by_depth[2] },
		{ label: 'Sulfur D1', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.sulfur_ppm_by_depth && s.profile_nitrogen.sulfur_ppm_by_depth[0] },
		{ label: 'Sulfur D2', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.sulfur_ppm_by_depth && s.profile_nitrogen.sulfur_ppm_by_depth[1] },
		{ label: 'Sulfur D3', unit: 'ppm', get: s => s.profile_nitrogen && s.profile_nitrogen.sulfur_ppm_by_depth && s.profile_nitrogen.sulfur_ppm_by_depth[2] }
	];
}

// Every optional (Turf Elite Plus) panel, in the exact order they appear in the report.
export function optionalPanelSections() {
	return [
		{ id: 'soilPhysicalHealth', title: 'Soil Physical & Health', columns: soilPhysicalHealthColumns() },
		{ id: 'saturatedExtract', title: 'Saturated Paste Extract', columns: saturatedExtractColumns() },
		{ id: 'waterSoluble', title: 'Water-Soluble Nutrients', columns: waterSolubleColumns() },
		{ id: 'h3aExtractable', title: 'H3A Extractable Nutrients', columns: h3aExtractableColumns() },
		{ id: 'totalCombustion', title: 'Total Combustion', columns: totalCombustionColumns() },
		{ id: 'profileNitrogen', title: 'Profile Nitrogen (by Depth)', columns: profileNitrogenColumns() }
	];
}

// The 9 nutrient bar-chart specs (pH and CEC/texture are handled separately in
// ChartGrid.vue since they use different band logic).
export function nutrientChartSpecs(optimumRanges) {
	return [
		{ title: 'Phosphorus', unit: 'ppm', range: optimumRanges.phosphorus_ppm, get: s => s.phosphorus && s.phosphorus.value_ppm },
		{ title: 'Potassium', unit: 'ppm', range: optimumRanges.potassium_ppm, get: s => s.potassium_ppm },
		{ title: 'Calcium', unit: 'ppm', range: optimumRanges.calcium_ppm, get: s => s.calcium_ppm },
		{ title: 'Magnesium', unit: 'ppm', range: optimumRanges.magnesium_ppm, get: s => s.magnesium_ppm },
		{ title: 'Zinc', unit: 'ppm', range: optimumRanges.zinc_ppm, get: s => s.zinc_ppm && s.zinc_ppm.value },
		{ title: 'Manganese', unit: 'ppm', range: optimumRanges.manganese_ppm, get: s => s.manganese_ppm && s.manganese_ppm.value },
		{ title: 'Copper', unit: 'ppm', range: optimumRanges.copper_ppm, get: s => s.copper_ppm && s.copper_ppm.value },
		{ title: 'Iron', unit: 'ppm', range: optimumRanges.iron_ppm, get: s => s.iron_ppm && s.iron_ppm.value },
		{ title: 'Sulfur', unit: 'ppm', range: optimumRanges.sulfur_ppm, get: s => s.sulfur_ppm && s.sulfur_ppm.value }
	];
}
